/**
 * Sender outbox (Signal-style retry-until-ACK). A port of mobile
 * `ChatRoomProvider`'s send path (`sendMessage` / `_attemptSend` /
 * `retryMessage` / `flushOutbox`) without the UI state — the React hooks
 * in T5.3/T5.4 own the bubbles, this module owns the network + store truth.
 *
 * Flow per message: build the temp (id == clientMessageId, like mobile's
 * `queuedMessage`), enqueue it so a dead network or killed app cannot lose
 * it, POST it, and on ACK swap in the authoritative server message and
 * dequeue the row. Failures bump `attempts` and rethrow so the caller can
 * flag the bubble with tap-to-retry; the row stays for `flushOutbox`.
 */
import { api } from '@/lib/api'
import {
  bumpOutboxAttempts,
  deleteOutbox,
  getAllOutbox,
  getOutbox,
  type OutboxRow,
} from './chatDb'
import { parseMessage, type ChatMessage } from './types'

export interface OutgoingMessage {
  conversationId: string
  senderId: string
  content: string
  imageUrl?: string
}

/** Mobile `_uuidV4`; prefers `crypto.randomUUID` when available. */
export function newClientMessageId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to the manual generator
  }
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** The optimistic `sending` bubble plus its outbox row (id == clientMessageId). */
export function buildOutgoing(outgoing: OutgoingMessage, clientMessageId?: string): {
  message: ChatMessage
  row: OutboxRow
} {
  const id = clientMessageId ?? newClientMessageId()
  const createdAt = new Date().toISOString()
  return {
    message: {
      id,
      conversationId: outgoing.conversationId,
      senderId: outgoing.senderId,
      content: outgoing.content,
      imageUrl: outgoing.imageUrl,
      isRead: false,
      clientMessageId: id,
      status: 'sending',
      createdAt,
    },
    row: {
      id,
      conversation_id: outgoing.conversationId,
      sender_id: outgoing.senderId,
      content: outgoing.content,
      image_url: outgoing.imageUrl,
      created_at: Date.parse(createdAt),
      attempts: 0,
    },
  }
}

/**
 * Mobile `_attemptSend`: POST the row, return the authoritative server
 * message and dequeue (the ACK). Failure bumps `attempts` and rethrows.
 */
export async function attemptSend(row: OutboxRow): Promise<ChatMessage> {
  try {
    const res = await api.post(`/conversations/${row.conversation_id}/messages`, {
      content: row.content,
      ...(row.image_url ? { imageUrl: row.image_url } : {}),
      clientMessageId: row.id,
    })
    // The backend returns the MessageDto directly, not wrapped. A null or
    // non-Map body (error HTML, empty 204) is a failed attempt, like mobile.
    const serverMessage = parseMessage(res.data)
    if (!serverMessage) throw new Error('Unexpected response shape')
    await deleteOutbox(row.id)
    return serverMessage
  } catch (err) {
    await bumpOutboxAttempts(row.id)
    throw err
  }
}

/**
 * Mobile `flushOutbox`: re-attempt every queued row for the conversation
 * (called on chat open). One row's failure never stops the rest.
 *
 * Callers compose the send as mobile's `sendMessage` does: `buildOutgoing`
 * for the optimistic bubble, `enqueueOutbox`, then `attemptSend`, swapping
 * the bubble for the ACK (or flagging tap-to-retry on rejection).
 */
export async function flushOutbox(
  conversationId: string,
  onAck?: (serverMessage: ChatMessage, tempId: string) => void,
): Promise<{ sent: number; failed: number }> {
  const rows = await getOutbox(conversationId)
  let sent = 0
  let failed = 0
  for (const row of rows) {
    try {
      const serverMessage = await attemptSend(row)
      sent += 1
      onAck?.(serverMessage, row.id)
    } catch {
      failed += 1
    }
  }
  return { sent, failed }
}

/**
 * Reconnect flush: retry every queued row across conversations (called
 * on the browser `online` event). One row's failure never stops the rest.
 */
export async function flushAllOutboxes(
  onAck?: (serverMessage: ChatMessage, tempId: string) => void,
): Promise<{ sent: number; failed: number }> {
  const rows = await getAllOutbox()
  let sent = 0
  let failed = 0
  for (const row of rows) {
    try {
      const serverMessage = await attemptSend(row)
      sent += 1
      onAck?.(serverMessage, row.id)
    } catch {
      failed += 1
    }
  }
  return { sent, failed }
}
